import { Injectable } from '@angular/core';
import { Router, NavigationExtras } from "@angular/router";
import { Observable, Subject, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { LocalStoreManager } from './local-store-manager.service';
import { ConfigurationService } from './configuration.service';
import { DBkeys } from './db-keys';
import { JwtHelper } from './jwt-helper';
import { Utilities } from './utilities';
import { LoginResponse, IdToken } from '../models/login-response.model';
import { User } from '../models/user.model';
import { UserLogin } from '../models/user-login.model';
import { OidcHelperService } from './oidc-helper.service';
import { PermissionValues } from '../models/permission.model';

export type OidcProviders = 'google' | 'facebook' | 'twitter' | 'microsoft';

@Injectable()
export class AuthService {
    public get loginUrl() { return this.configurations.loginUrl; }
    public get homeUrl() { return this.configurations.homeUrl; }

    public loginRedirectUrl: string | null = null;
    public logoutRedirectUrl: string | null = null;

    public reLoginDelegate: { (): void } | undefined;

    private previousIsLoggedInCheck = false;
    private loginStatus = new Subject<boolean>();

    constructor(
        private router: Router,
        private oidcHelperService: OidcHelperService,
        private configurations: ConfigurationService,
        private localStorage: LocalStoreManager) {
        this.initializeLoginStatus();
    }

    private initializeLoginStatus() {
        this.localStorage.getInitEvent().subscribe(() => {
            this.reevaluateLoginStatus();
        });
    }

    gotoPage(page: string, preserveParams = true) {

        let navigationExtras: NavigationExtras = {
            queryParamsHandling: preserveParams ? "merge" : "", preserveFragment: preserveParams
        };

        this.router.navigate([page], navigationExtras);
    }

    redirectLoginUser() {
        const redirect = this.loginRedirectUrl && this.loginRedirectUrl !== '/' &&
            this.loginRedirectUrl !== ConfigurationService.defaultHomeUrl ? this.loginRedirectUrl : this.homeUrl;
        this.loginRedirectUrl = null;

        const urlParamsAndFragment = Utilities.splitInTwo(redirect, '#');
        const urlAndParams = Utilities.splitInTwo(urlParamsAndFragment.firstPart, '?');

        const navigationExtras: NavigationExtras = {
            fragment: urlParamsAndFragment.secondPart,
            queryParams: urlAndParams.secondPart ? Utilities.getQueryParamsFromString(urlAndParams.secondPart) : null,
            queryParamsHandling: 'merge'
        };

        this.router.navigate([urlAndParams.firstPart], navigationExtras);
    }

    redirectLogoutUser() {
        const redirect = this.logoutRedirectUrl ? this.logoutRedirectUrl : this.loginUrl;
        this.logoutRedirectUrl = null;

        this.router.navigate([redirect]);
    }

    redirectForLogin() {
        this.loginRedirectUrl = this.router.url;
        this.router.navigate([this.loginUrl]);
    }

    reLogin() {
        if (this.reLoginDelegate) {
            this.reLoginDelegate();
        }
        else {
            this.redirectForLogin();
        }
    }

    refreshLogin() {
        return this.oidcHelperService.refreshLogin()
            .pipe(map(resp => this.processLoginResponse(resp, this.rememberMe)));
    }

    loginWithPassword(user: UserLogin) {
        if (this.isLoggedIn) {
            this.logout();
        }

        return this.oidcHelperService.loginWithPassword(user.userName, user.password)
            .pipe(map(resp => this.processLoginResponse(resp, user.rememberMe)));
    }

    loginWithExternalToken(token: string, provider: string, email?: string | null, password?: string | null) {
        if (this.isLoggedIn) {
            this.logout();
        }

        return this.oidcHelperService.loginWithExternalToken(token, provider, email, password)
            .pipe(map(resp => this.processLoginResponse(resp)));
    }

    initLoginWithGoogle(rememberMe?: boolean) {
        if (this.isLoggedIn) {
            this.logout();
        }

        this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
        this.oidcHelperService.initLoginWithGoogle();
    }

    initLoginWithFacebook(rememberMe?: boolean) {
        if (this.isLoggedIn) {
            this.logout();
        }

        this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
        this.oidcHelperService.initLoginWithFacebook();
    }


    initLoginWithMicrosoft(rememberMe?: boolean) {
        if (this.isLoggedIn) {
            this.logout();
        }

        this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
        this.oidcHelperService.initLoginWithMicrosoft();
    }

    processExternalOidcLoginTokens(provider: OidcProviders) {
        if (provider === 'google') {
            return this.oidcHelperService.processGoogleLoginTokens();
        } else if (provider === 'facebook') {
            return this.oidcHelperService.processFacebookLoginTokens();
        } else if (provider === 'microsoft') {
            return this.oidcHelperService.processMicrosoftLoginTokens();
        }
        else {
            return throwError(() => new Error(`Unknown OIDC Provider "${provider}"`));
        }
    }

    initLoginWithTwitter(rememberMe?: boolean) {
        if (this.isLoggedIn) {
            this.logout();
        }

        this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
        this.oidcHelperService.initLoginWithTwitter();
    }

    getTwitterAccessToken(oauthToken: string, oauthVerifier: string) {
        return this.oidcHelperService.getTwitterAccessToken(oauthToken, oauthVerifier);
    }

    private processLoginResponse(response: LoginResponse, rememberMe?: boolean) {
        const idToken = response.id_token;
        const accessToken = response.access_token;
        const refreshToken = response.refresh_token;

        if (idToken == null) {
            throw new Error('idToken cannot be null');
        }

        if (accessToken == null) {
            throw new Error('accessToken cannot be null');
        }

        rememberMe = rememberMe ?? this.rememberMe;

        const accessTokenExpiry = new Date();
        accessTokenExpiry.setSeconds(accessTokenExpiry.getSeconds() + response.expires_in);

        const jwtHelper = new JwtHelper();
        const decodedIdToken = jwtHelper.decodeToken(idToken) as IdToken;

        const permissions: PermissionValues[] = Array.isArray(decodedIdToken.permission) ?
            decodedIdToken.permission : [decodedIdToken.permission];

        if (!this.isLoggedIn) {
            this.configurations.import(decodedIdToken.configuration);
        }

        const user = new User(
            decodedIdToken.sub,
            decodedIdToken.name,
            decodedIdToken.fullname,
            decodedIdToken.email,
            decodedIdToken.jobtitle,
            decodedIdToken.phone_number,
            Array.isArray(decodedIdToken.role) ? decodedIdToken.role : [decodedIdToken.role],
            true,
            decodedIdToken.regions);

        user.isEnabled = true;

        this.saveUserDetails(user, permissions, accessToken, refreshToken, accessTokenExpiry, rememberMe);

        this.reevaluateLoginStatus(user);

        return user;
    }

    private saveUserDetails(user: User, permissions: string[], accessToken: string, refreshToken: string, expiresIn: Date, rememberMe: boolean) {
        if (rememberMe) {
            this.localStorage.savePermanentData(accessToken, DBkeys.ACCESS_TOKEN);
            this.localStorage.savePermanentData(refreshToken, DBkeys.REFRESH_TOKEN);
            this.localStorage.savePermanentData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
            this.localStorage.savePermanentData(permissions, DBkeys.USER_PERMISSIONS);
            this.localStorage.savePermanentData(user, DBkeys.CURRENT_USER);
        }
        else {
            this.localStorage.saveSyncedSessionData(accessToken, DBkeys.ACCESS_TOKEN);
            this.localStorage.saveSyncedSessionData(refreshToken, DBkeys.REFRESH_TOKEN);
            this.localStorage.saveSyncedSessionData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
            this.localStorage.saveSyncedSessionData(permissions, DBkeys.USER_PERMISSIONS);
            this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
        }

        this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
    }

    logout(): void {
        this.localStorage.deleteData(DBkeys.ACCESS_TOKEN);
        this.localStorage.deleteData(DBkeys.REFRESH_TOKEN);
        this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);
        this.localStorage.deleteData(DBkeys.USER_PERMISSIONS);
        this.localStorage.deleteData(DBkeys.CURRENT_USER);

        this.configurations.clearLocalChanges();

        this.reevaluateLoginStatus();
    }

    private reevaluateLoginStatus(currentUser?: User | null) {
        const user = currentUser ?? this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
        const isLoggedIn = user != null;

        if (this.previousIsLoggedInCheck !== isLoggedIn) {
            setTimeout(() => {
                this.loginStatus.next(isLoggedIn);
            });
        }

        this.previousIsLoggedInCheck = isLoggedIn;
    }

    getLoginStatusEvent(): Observable<boolean> {
        return this.loginStatus.asObservable();
    }

    get currentUser(): User {

        const user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
        this.reevaluateLoginStatus(user);

        return user;
    }

    get userPermissions(): PermissionValues[] {
        return this.localStorage.getDataObject<PermissionValues[]>(DBkeys.USER_PERMISSIONS) ?? [];
    }

    userHasPermission(permissionValue: string): boolean {
        return this.userPermissions.some(p => p === permissionValue);
    }


    get accessToken(): string | null {
        return this.oidcHelperService.accessToken;
    }

    getAccessToken(): string {
        return this.oidcHelperService.accessToken;
    }
    
    get accessTokenExpiryDate(): Date | null {
        return this.oidcHelperService.accessTokenExpiryDate;
    }

    get refreshToken(): string | null {
        return this.oidcHelperService.refreshToken;
    }

    get isSessionExpired(): boolean {
        return this.oidcHelperService.isSessionExpired;
    }

    get isLoggedIn(): boolean {
        return this.currentUser != null;
    }

    get rememberMe(): boolean {
        return this.localStorage.getDataObject<boolean>(DBkeys.REMEMBER_ME) === true;
    }

    currentUserIsAtLeastAdministrator(): boolean {
        return this.currentUser.roles.some(t => t === "Administratör" || t === "Systemadministratör");
    }
}
